"use client";

import { CheckCheck, Download, FilterX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { WorkspacePage } from "@/components/ds/workspace-page";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSelectTriggerClass, TableHeaderSearch, ThemedSelect, themedSelectMenuClass } from "@/components/ui-system/select";
import { useAuthStore } from "@/modules/auth/store";

import {
  type ListNotificationsParams,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsQuery,
} from "../hooks";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const menuClassName = themedSelectMenuClass;
const optionClassName = "rounded-control text-caption";

function readNotificationString(payload: Record<string, unknown> | null, key: string): string {
  const value = payload?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function getNotificationDeviceLabel(notification: {
  payload: Record<string, unknown> | null;
}): { primary: string; secondary: string } | null {
  const serialNumber = readNotificationString(notification.payload, "device_serial_number");
  const deviceName = readNotificationString(notification.payload, "device_name");
  const deviceUid = readNotificationString(notification.payload, "device_uid");
  const primary = deviceName || deviceUid || serialNumber;
  const secondary = serialNumber || deviceUid;
  if (!primary) {
    return null;
  }
  return { primary, secondary };
}

function getBackupDownloadUrl(notification: {
  payload: Record<string, unknown> | null;
  link?: string | null;
}): string {
  const payloadUrl = readNotificationString(notification.payload, "download_url");
  if (payloadUrl) {
    return payloadUrl;
  }
  return notification.link ?? "";
}

function getAlertSeverityStyle(notification: {
  type: string;
  payload: Record<string, unknown> | null;
  is_read: boolean;
}) {
  if (notification.type === "storage_quota") {
    return {
      rowClassName: notification.is_read ? "bg-warning/5" : "bg-warning/10",
      badgeClassName: notification.is_read ? "bg-warning/15 text-warning" : "bg-warning/25 text-warning",
    };
  }
  if (notification.type !== "alert_triggered") {
    return {
      rowClassName: "",
      badgeClassName: notification.is_read ? "bg-surface-secondary text-muted" : "bg-error/10 text-error",
    };
  }

  const severity = typeof notification.payload?.severity === "string"
    ? notification.payload.severity.toLowerCase()
    : "";

  if (severity === "info") {
    return {
      rowClassName: notification.is_read ? "bg-info/5" : "bg-info/10",
      badgeClassName: notification.is_read ? "bg-info/10 text-info" : "bg-info/20 text-info",
    };
  }
  if (severity === "critical") {
    return {
      rowClassName: notification.is_read ? "bg-error/5" : "bg-error/10",
      badgeClassName: notification.is_read ? "bg-error/10 text-error" : "bg-error/20 text-error",
    };
  }
  if (severity === "warning") {
    return {
      rowClassName: notification.is_read ? "bg-warning/5" : "bg-warning/10",
      badgeClassName: notification.is_read ? "bg-warning/15 text-warning" : "bg-warning/25 text-warning",
    };
  }

  return {
    rowClassName: "",
    badgeClassName: notification.is_read ? "bg-surface-secondary text-muted" : "bg-error/10 text-error",
  };
}

function formatRelative(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.max(0, Math.floor(diffMs / 60000));
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function buildActiveFilterCount(filters: ListNotificationsParams): number {
  let count = 0;
  if (filters.search?.trim()) count += 1;
  if (filters.readState && filters.readState !== "all") count += 1;
  if (filters.severity && filters.severity !== "all") count += 1;
  if (filters.type?.trim()) count += 1;
  return count;
}

export function NotificationsPage() {
  const token = useAuthStore((s) => s.token);
  const [toast, setToast] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [readState, setReadState] = useState<"all" | "read" | "unread">("all");
  const [severity, setSeverity] = useState<"all" | "info" | "warning" | "critical">("all");
  const [type, setType] = useState("all");

  const offset = (page - 1) * pageSize;
  const filters = useMemo<ListNotificationsParams>(
    () => ({
      limit: pageSize,
      offset,
      search,
      readState,
      severity,
      type: type === "all" ? "" : type,
    }),
    [offset, pageSize, readState, search, severity, type],
  );
  // Notifications are personal. When a platform actor has no workspace
  // selected, the API resolves their default accessible workspace rather than
  // presenting an unusable "all workspaces" scope.
  const notificationsEnabled = Boolean(token);
  const query = useNotificationsQuery(filters, notificationsEnabled);
  const markOneRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const items = query.data?.items ?? [];
  const activeFilterCount = buildActiveFilterCount(filters);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, search, readState, severity, type]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setReadState("all");
    setSeverity("all");
    setType("all");
  };

  return (
    <WorkspacePage className="pt-0">
      <ListSurface>
        <ListSurfaceHeader
          title="Notifications"
          meta={<span>{query.data?.unread_count ?? 0} unread</span>}
          controls={
            <ListToolbar>
              <ListToolbarGroup grow>
                <TableHeaderSearch
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder="Search notifications"
                />
                <ThemedSelect
                  value={readState}
                  onChange={(value) => setReadState(value as typeof readState)}
                  ariaLabel="Read state filter"
                  className="w-[8rem] shrink-0"
                  triggerClassName={tableHeaderSelectTriggerClass}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                  options={[
                    { value: "all", label: "Any state" },
                    { value: "unread", label: "Unread" },
                    { value: "read", label: "Read" },
                  ]}
                />
                <ThemedSelect
                  value={severity}
                  onChange={(value) => setSeverity(value as typeof severity)}
                  ariaLabel="Severity filter"
                  className="w-[8rem] shrink-0"
                  triggerClassName={tableHeaderSelectTriggerClass}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                  options={[
                    { value: "all", label: "Any severity" },
                    { value: "critical", label: "Critical" },
                    { value: "warning", label: "Warning" },
                    { value: "info", label: "Info" },
                  ]}
                />
                <ThemedSelect
                  value={type}
                  onChange={setType}
                  ariaLabel="Notification type filter"
                  className="w-[8rem] shrink-0"
                  triggerClassName={tableHeaderSelectTriggerClass}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                  options={[
                    { value: "all", label: "Any type" },
                    { value: "alert_triggered", label: "Alert triggered" },
                    { value: "storage_quota", label: "Storage quota" },
                  ]}
                />
              </ListToolbarGroup>
              <ToolbarIconButton
                icon={CheckCheck}
                label="Mark all read"
                disabled={(query.data?.unread_count ?? 0) === 0 || markAllRead.isPending}
                onClick={() => {
                  void markAllRead.mutateAsync().then(() => setToast("All notifications marked as read."));
                }}
              />
                {activeFilterCount > 0 ? (
                  <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={clearFilters} />
                ) : null}
              </ListToolbar>
            }
          />
        <ListSurfaceBody>
        {query.isLoading ? (
          <div className="p-ds-4" aria-label="Loading notifications">
            {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={3} />)}
          </div>
        ) : query.isError ? (
          <div className="px-ds-5 py-ds-8 text-caption text-error">Failed to load notifications.</div>
        ) : items.length === 0 ? (
          <div className="px-ds-5 py-ds-12 text-center">
            <p className="text-caption font-medium text-foreground">No notifications match this view.</p>
            <p className="mt-2 text-micro text-muted">
              Try clearing filters or wait for new inbox events to arrive.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {items.map((notification) => {
              const severityStyle = getAlertSeverityStyle(notification);
              const deviceLabel = getNotificationDeviceLabel(notification);
              const backupDownloadUrl = notification.type === "backup" ? getBackupDownloadUrl(notification) : "";
              return (
                <li
                  key={notification.id}
                  className={`flex items-start justify-between gap-ds-4 px-ds-4 py-ds-3 ${severityStyle.rowClassName}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-ds-2">
                      <span
                        className={`inline-flex min-h-6 items-center rounded-full px-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.08em] ${severityStyle.badgeClassName}`}
                      >
                        {notification.is_read ? "Read" : "Unread"}
                      </span>
                      <span className="text-micro text-muted">{formatRelative(notification.created_at)}</span>
                    </div>
                    {notification.link ? (
                      <Link to={notification.link} className="mt-1.5 block text-sm font-semibold leading-5 text-foreground hover:text-link">
                        {notification.title}
                      </Link>
                    ) : (
                      <p className="mt-1.5 text-sm font-semibold leading-5 text-foreground">{notification.title}</p>
                    )}
                    {notification.body ? <p className="mt-0.5 text-caption leading-5 text-muted">{notification.body}</p> : null}
                    {deviceLabel ? (
                      <div className="mt-1.5">
                        <p className="text-caption font-medium text-foreground">{deviceLabel.primary}</p>
                        {deviceLabel.secondary && deviceLabel.secondary !== deviceLabel.primary ? (
                          <p className="text-micro text-muted">{deviceLabel.secondary}</p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <button
                      type="button"
                      className="rounded-control border border-card-border bg-surface-secondary px-2.5 py-1 text-micro font-medium text-muted transition-colors hover:border-border-strong hover:text-foreground"
                      title={new Date(notification.created_at).toLocaleString()}
                    >
                      {new Date(notification.created_at).toLocaleString()}
                    </button>
                    {!notification.is_read ? (
                      <button
                        type="button"
                        disabled={markOneRead.isPending}
                        onClick={() => {
                          void markOneRead.mutateAsync(notification.id).then(() => setToast("Notification marked as read."));
                        }}
                        className="rounded-control border border-card-border bg-surface-primary px-2.5 py-1 text-micro font-semibold text-primary transition-colors hover:border-primary/20 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Mark read
                      </button>
                    ) : null}
                    {backupDownloadUrl ? (
                      <Link
                        to={backupDownloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                          if (!notification.is_read) {
                            void markOneRead.mutateAsync(notification.id);
                          }
                        }}
                        className="inline-flex min-h-9 items-center gap-1.5 rounded-control bg-primary px-3 text-micro font-semibold text-primary-foreground shadow-raised transition-colors hover:bg-primary/90"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download
                      </Link>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        </ListSurfaceBody>

          <ListSurfaceFooter
            left={
              <label className="flex items-center gap-ds-2">
                <span className="text-caption text-muted">Rows per page</span>
                <ThemedSelect
                  value={String(pageSize)}
                  ariaLabel="Rows per page"
                  className="w-[5.5rem]"
                  triggerClassName={tableHeaderSelectTriggerClass}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                  options={PAGE_SIZE_OPTIONS.map((size) => ({ value: String(size), label: String(size) }))}
                  onChange={(nextValue) => setPageSize(Number(nextValue) as (typeof PAGE_SIZE_OPTIONS)[number])}
                />
              </label>
            }
            right={
              <>
                <span className="text-caption text-muted">
                  Showing {items.length === 0 ? 0 : offset + 1} to {Math.min(offset + items.length, total)} of {total}
                </span>
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  className="inline-flex min-h-10 items-center rounded-xl border border-border px-4 text-sm font-semibold text-foreground transition-colors hover:border-border-strong hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Previous
                </button>
                <div className="min-w-[7rem] text-center text-caption text-muted">
                  Page {page} of {totalPages}
                </div>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  className="inline-flex min-h-10 items-center rounded-xl border border-border px-4 text-sm font-semibold text-foreground transition-colors hover:border-border-strong hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Next
                </button>
              </>
            }
          />
      </ListSurface>

      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </WorkspacePage>
  );
}
